type OfflineSyncTriggerScheduler = (callback: () => void, delayMs: number) => unknown;

export interface OfflineSyncTriggerQueueDependencies {
  isOnline: () => boolean;
  isSyncing: () => boolean;
  performFullSync: () => Promise<void>;
  logger?: Pick<Console, 'error'>;
  schedule?: OfflineSyncTriggerScheduler;
}

export class OfflineSyncTriggerQueue {
  private readonly QUEUED_SYNC_DELAY_MS = 60 * 1000;
  private readonly pendingReviewIds = new Set<number>();
  private readonly schedule: OfflineSyncTriggerScheduler;
  private queuedSyncScheduled = false;
  private queuedSyncVersion = 0;

  constructor(private readonly dependencies: OfflineSyncTriggerQueueDependencies) {
    this.schedule = dependencies.schedule ?? ((callback, delayMs) => setTimeout(callback, delayMs));
  }

  enqueue(reviewId: number): void {
    this.pendingReviewIds.add(reviewId);
    this.scheduleQueuedSync();
  }

  hasPending(): boolean {
    return this.pendingReviewIds.size > 0;
  }

  scheduleDefensiveSync(): void {
    this.scheduleFullSyncIfReady(500, 'Defensive sync', {
      requirePendingWork: false,
    });
  }

  clear(): void {
    this.pendingReviewIds.clear();
    this.queuedSyncScheduled = false;
    this.queuedSyncVersion += 1;
  }

  private scheduleQueuedSync(): void {
    if (this.queuedSyncScheduled) {
      return;
    }

    this.scheduleFullSyncIfReady(this.QUEUED_SYNC_DELAY_MS, 'Queued sync', {
      coalesceQueuedSync: true,
    });
  }

  private scheduleFullSyncIfReady(
    delayMs: number,
    label: string,
    options: { coalesceQueuedSync?: boolean; requirePendingWork?: boolean } = {}
  ): void {
    if (!this.dependencies.isOnline() || this.dependencies.isSyncing()) {
      return;
    }

    const requirePendingWork = options.requirePendingWork ?? true;
    const syncVersion = this.queuedSyncVersion;
    if (options.coalesceQueuedSync) {
      this.queuedSyncScheduled = true;
    }

    this.schedule(() => {
      if (options.coalesceQueuedSync) {
        this.queuedSyncScheduled = false;
      }

      if (
        syncVersion !== this.queuedSyncVersion ||
        (requirePendingWork && !this.hasPending()) ||
        !this.dependencies.isOnline()
      ) {
        return;
      }

      if (this.dependencies.isSyncing()) {
        if (options.coalesceQueuedSync) {
          this.scheduleQueuedSync();
        }
        return;
      }

      this.dependencies.performFullSync()
        .then(() => {
          this.clear();
        })
        .catch(error => {
          this.dependencies.logger?.error(`[OfflineReviewService] ${label} failed:`, error);
        });
    }, delayMs);
  }
}
